import { omit } from 'lodash';
import type { HierarchicalItem, HierarchicalItemMenu, MenuItem, TreeItem } from '@martech-solution/utils/types';
import { v4 as uuid } from 'uuid';
import commonPath from 'src/constants/urlPath/erp/commonPath';

type HierarchyType<T> = {
  id: number | string;
  parentId?: number | string | null;
  level?: number;
  children?: T[];
};

/** ================= BUILD MENU ================= */
export function buildHierarchyMenu(
  flatArray: HierarchicalItemMenu[] | undefined,
  currentResourceId?: number
): MenuItem[] | undefined {
  if (!flatArray) return undefined;

  const map = new Map<string, HierarchicalItemMenu>();
  const roots: HierarchicalItemMenu[] = [];

  // First pass: create map
  flatArray.forEach((node) => {
    const { id, ...rest } = node;
    map.set(id.toString(), { ...node, children: [] });
  });

  // Second pass: link parent-child
  map.forEach((node) => {
    if (!node.parentId || currentResourceId === node.parentId) {
      roots.push(node);
    } else {
      const parent = map.get(node.parentId.toString());
      if (parent) parent.children!.push(node);
      else roots.push(node); // orphan
    }
  });

  // Convert to MenuItem
  return roots.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)).map((item) => convertToMenuItem(item));
}

function convertToMenuItem(item: HierarchicalItemMenu): MenuItem {
  const menuItem: MenuItem = {
    key: item.path || `undefined-${uuid()}`,
    label: item.name,
    disabled: !item.children?.length && !item.path,
    children: item.children?.length
      ? item.children.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)).map(convertToMenuItem)
      : undefined
  };
  return menuItem;
}

/** ================= BUILD TREE SELECT ================= */
export function buildHierarchySelect(
  flatArray: HierarchicalItem[] | undefined,
  isDisableParent?: boolean
): TreeItem[] | undefined {
  if (!flatArray) return undefined;

  const map = new Map<string, HierarchicalItem>();
  const roots: HierarchicalItem[] = [];

  // First pass: create map
  flatArray.forEach((node) => map.set(node.id.toString(), { ...node, children: [] }));

  // Second pass: link parent-child
  map.forEach((node) => {
    if (!node.parentId) {
      roots.push(node);
    } else {
      const parent = map.get(node.parentId.toString());
      if (parent) parent.children!.push(node);
      else roots.push(node); // orphan
    }
  });

  return roots.map((item) => convertToTreeItem(item, isDisableParent));
}

function convertToTreeItem(item: HierarchicalItem, isDisableParent?: boolean): TreeItem {
  const treeItem: TreeItem = {
    key: item.id,
    value: item.id.toString(),
    title: item.name,
    data: omit(item, ['children', 'parentId']),
    disabled: isDisableParent ? !!item.children?.length : false,
    children: item.children?.map((child) => convertToTreeItem(child, isDisableParent)) ?? []
  };
  return treeItem;
}

/** ================= GENERIC HIERARCHY ================= */
export function buildHierarchy<T extends HierarchyType<T>>(flatArray: T[] | undefined): T[] | undefined {
  if (!flatArray) return undefined;
  if (!flatArray.length) return [];

  const filteredArray = flatArray.filter((item) => {
    const itemWithPath = item as T & { path?: string };
    return itemWithPath.path !== commonPath.erp;
  });

  const map = new Map<string, T>();
  const roots: T[] = [];

  // First pass: create map
  filteredArray.forEach((node) => map.set(String(node.id), { ...node, children: [], level: 0 } as T));

  // Second pass: link parent-child
  filteredArray.forEach((node) => {
    const currentNode = map.get(String(node.id))!;
    if (node.parentId != null && node.parentId !== '') {
      const parent = map.get(String(node.parentId));
      if (parent) {
        currentNode.level = (parent.level ?? 0) + 1;
        parent.children!.push(currentNode);
      } else {
        currentNode.level = 0;
        roots.push(currentNode); // orphan
      }
    } else {
      currentNode.level = 0;
      roots.push(currentNode);
    }
  });

  return roots;
}

/** ================= FIND MENU ITEM ================= */
export function findMenuItem(menuItems: MenuItem[], key: string): MenuItem | undefined {
  for (const item of menuItems) {
    if (item.key === key) return item;
    if (item.children) {
      const found = findMenuItem(item.children, key);
      if (found) return found;
    }
  }
  return undefined;
}
